import { useFetchApplicationData } from "@/hooks/useApplicationQueries";
import supabase from "@/supabase";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { CURRENT_JOB_ID_KEY } from "./ActiveJobContext";

// Local storage key for current application ID
const CURRENT_APPLICATION_ID_KEY = "currentApplicationId";

// Define the Application type
export interface Application {
  id?: string;
  applicationId?: string;
  airtablePersonId?: string;
  jobId?: string; // ID of the job being applied for
  stage: string;
  currentStage: string;
  currentSegment: string | null;
  stageNote: string;
  referenceDriverId?: string;
  contractSalary?: string;
  basicDetails: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    dateOfBirth: string;
    sex?: string;
    rightToWork?: boolean;
    validDrivingLicense?: boolean;
    ownElectricVan?: boolean;
    needsVan: boolean;
    workDays: string[];
    workDaysRange: string;
    referenceDriverId?: string;
    cv?: string;
  };
  personalDetails: {
    bankName: string;
    accountNumber: string;
    sortCode: string;
    accountName: string;
    proofOfBankDetails?: string;
    nextOfKin: {
      name: string;
      phone: string;
      relation: string;
    };
    nationalInsuranceNumber: string;
    utrNumber?: string;
    company: {
      type: string;
      number?: string;
      vatNumber?: string;
    };
    address: {
      line1: string;
      line2?: string;
      city: string;
      postcode: string;
    };
    calendlyFeedback?: string;
  };
  formsCompleted: {
    vehicleReleaseForm: boolean;
    driverContract: boolean;
    onboardingAgreement?: boolean;
  };
  assessmentResults: {
    sjtScore: number;
    driverOnroadTestScore?: number;
  };
  documents: {
    drivingLicenceNumber?: string;
    licenseFront?: string;
    licenseBack?: string;
    rtwShareCode?: string;
    passportOrBirthCertificate?: string;
    insuranceProvider?: string;
    policyNumber?: string;
    insuranceProof?: string;
    headshot?: string;
    vehicleReleaseForm?: string;
    // New optional documents
    cpcCertificate?: string;
    cpcFront?: string;
    cpcBack?: string;
    tachoCard?: string;
    tachoFront?: string;
    tachoBack?: string;
    tachoCardNumber?: string;
    d4MedicalReport?: string;
  };
  orientation: {
    date?: string;
    location?: string;
    quizScore?: number;
    depot?: {
      name: string;
      address: string;
      city: string;
    };
  };
  vehicleCollection?: {
    date?: string;
    location?: string;
    depot?: {
      name: string;
      address: string;
      city: string;
    };
  };
  vehicleBooking?: {
    paymentMethodId?: string;
    stripeCustomerId?: string;
    depositAmount?: number;
    depositPaid?: boolean;
    bookingDate?: string;
  };
}

const NEW_APPLICATION: Application = {
  id: "",
  applicationId: "",
  airtablePersonId: "",
  jobId: "",
  stage: "basic-details",
  currentStage: "basic-details",
  currentSegment: null,
  stageNote: "",
  referenceDriverId: "",
  basicDetails: {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    dateOfBirth: "",
    sex: "",
    rightToWork: undefined,
    validDrivingLicense: undefined,
    ownElectricVan: undefined,
    needsVan: undefined,
    workDays: [],
    workDaysRange: "",
    cv: "",
  },
  personalDetails: {
    bankName: "",
    accountNumber: "",
    sortCode: "",
    accountName: "",
    proofOfBankDetails: "",
    nextOfKin: {
      name: "",
      phone: "",
      relation: "",
    },
    nationalInsuranceNumber: "",
    utrNumber: "",
    company: {
      type: "",
      number: "",
      vatNumber: "",
    },
    address: {
      line1: "",
      line2: "",
      city: "",
      postcode: "",
    },
    calendlyFeedback: "",
  },
  formsCompleted: {
    vehicleReleaseForm: false,
    driverContract: false,
    onboardingAgreement: false,
  },
  assessmentResults: {
    sjtScore: 0,
  },
  documents: {
    drivingLicenceNumber: "",
    licenseFront: "",
    licenseBack: "",
    rtwShareCode: "",
    passportOrBirthCertificate: "",
    insuranceProvider: "",
    policyNumber: "",
    insuranceProof: "",
    cpcCertificate: "",
    cpcFront: "",
    cpcBack: "",
    tachoCard: "",
    tachoFront: "",
    tachoBack: "",
    tachoCardNumber: "",
    d4MedicalReport: "",
  },
  orientation: {
    date: "",
    location: "",
    quizScore: 0,
    depot: {
      name: "",
      address: "",
      city: "",
    },
  },
  vehicleCollection: {
    date: "",
    location: "",
    depot: {
      name: "",
      address: "",
      city: "",
    },
  },
  vehicleBooking: {
    paymentMethodId: "",
    stripeCustomerId: "",
    depositAmount: 0,
    depositPaid: false,
    bookingDate: "",
  },
};

interface ApplicationContextProps {
  application?: Application;
  generateApplicationId: () => Promise<string>;
  setApplication: React.Dispatch<React.SetStateAction<Application | undefined>>;
  isLoading: boolean;
  error: unknown;
  updateApplicationIdAndRefetch: (
    applicationId: string,
    redirectPath?: string
  ) => Promise<void>;
  refetch: () => Promise<void>;
}

const ApplicationContext = createContext<ApplicationContextProps | undefined>(
  undefined
);

interface ApplicationProviderProps {
  children: ReactNode;
}

const ApplicationProvider: React.FC<ApplicationProviderProps> = ({
  children,
}) => {
  // Get current application ID from local storage
  const [currentApplicationId, setCurrentApplicationId] = useState<
    string | undefined
  >(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem(CURRENT_APPLICATION_ID_KEY) || undefined;
    }
    return undefined;
  });

  const {
    data: applicationResult,
    isLoading,
    error,
    refetch,
  } = useFetchApplicationData(currentApplicationId);

  const [application, setApplication] = useState<Application>(NEW_APPLICATION);

  // Update application state when data is fetched
  useEffect(() => {
    if (applicationResult?.application) {
      console.log("Application data fetched:", applicationResult.application);
      setApplication(applicationResult.application);
    }
  }, [applicationResult]);

  // Store applicationId in local storage when it changes
  useEffect(() => {
    if (application?.applicationId) {
      localStorage.setItem(
        CURRENT_APPLICATION_ID_KEY,
        application.applicationId
      );
      localStorage.setItem(CURRENT_JOB_ID_KEY, application.jobId || "");
      setCurrentApplicationId(application.applicationId);
    }
  }, [application?.applicationId, application?.jobId]);

  // Check if an application ID exists in the database
  const checkApplicationIdExists = useCallback(
    async (applicationId: string): Promise<boolean> => {
      try {
        const { data, error } = await supabase
          .from("applications")
          .select("application_id")
          .eq("application_id", applicationId)
          .maybeSingle();

        if (error) {
          console.error("Error checking application ID:", error);
          return false; // Assume it doesn't exist if there's an error
        }

        return !!data; // Return true if data exists (ID is taken), false otherwise
      } catch (error) {
        console.error("Unexpected error checking application ID:", error);
        return false;
      }
    },
    []
  );

  // Generate a unique application ID
  const generateApplicationId = useCallback(async (): Promise<string> => {
    let isUnique = false;
    let newId = "";

    // Keep generating IDs until we find a unique one
    while (!isUnique) {
      newId =
        "APP-" + Math.random().toString(36).substring(2, 15).toUpperCase();
      // Check if this ID already exists
      isUnique = !(await checkApplicationIdExists(newId));
    }

    return newId;
  }, [checkApplicationIdExists]);

  // Function to update application ID in local storage, refetch application and redirect
  const updateApplicationIdAndRefetch = useCallback(
    async (applicationId: string) => {
      // Update local storage
      localStorage.setItem(CURRENT_APPLICATION_ID_KEY, applicationId);

      // Update current application ID state which will trigger a refetch
      setCurrentApplicationId(applicationId);
      await refetch();
    },
    [refetch]
  );

  return (
    <ApplicationContext.Provider
      value={{
        application,
        generateApplicationId,
        setApplication,
        isLoading,
        error,
        updateApplicationIdAndRefetch,
        refetch,
      }}
    >
      {children}
    </ApplicationContext.Provider>
  );
};

const useApplication = (): ApplicationContextProps => {
  const context = useContext(ApplicationContext);
  if (!context) {
    throw new Error(
      "useApplication must be used within an ApplicationProvider"
    );
  }
  return context;
};

export { ApplicationProvider, useApplication, NEW_APPLICATION };
